import React from "react";

interface RadioBtnProps {
  label?: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: "default" | "green"; // 스타일 옵션 추가
}

interface RadioGroupProps {
  label?: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  required?: boolean;
  className?: string;
  direction?: "horizontal" | "vertical";
  style?: "default" | "green"; // 스타일 옵션 추가
}

// 단일 라디오 버튼 컴포넌트
export function RadioBtn({
  label,
  name,
  value,
  checked,
  onChange,
  disabled = false,
  required = false,
  className = "",
  style = "default",
}: RadioBtnProps) {
  const handleChange = () => {
    if (!disabled) {
      onChange(value);
    }
  };

  return (
    <label
      className={`
        flex items-center cursor-pointer select-none
        ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
        ${className}
      `}
    >
      <div className="relative">
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          className="sr-only"
        />

        {/* 커스텀 라디오 버튼 */}
        <div
          className={`
            w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
            ${
              style === "green"
                ? checked
                  ? disabled
                    ? "border-gray-400 bg-gray-100"
                    : "border-[#83C41C] bg-white"
                  : disabled
                    ? "border-gray-300 bg-gray-100"
                    : "border-[#666666] bg-white hover:border-[#83C41C]"
                : checked
                  ? disabled
                    ? "border-gray-400 bg-[#1E380B]"
                    : "border-[#CCC] bg-[#FFF]"
                  : disabled
                    ? "border-gray-300 bg-gray-100"
                    : "border-[#CCCCCC] bg-white hover:border-gray-400"
            }
          `}
        >
          {/* 내부 원 */}
          {checked && (
            <div
              className={`
                w-2 h-2 rounded-full
                ${
                  style === "green"
                    ? disabled
                      ? "bg-gray-400"
                      : "bg-[#83C41C]"
                    : disabled
                      ? "bg-white"
                      : "bg-[#1E380B]"
                }
              `}
            />
          )}
        </div>
      </div>

      {/* 라벨 */}
      {label && (
        <span
          className={`
            ml-2 text-base
            ${disabled ? "text-gray-400" : "text-[#333333]"}
          `}
        >
          {required && <span className="text-red-500 mr-1">*</span>}
          {label}
        </span>
      )}
    </label>
  );
}

// 라디오 그룹 컴포넌트
export function RadioGroup({
  label,
  name,
  value,
  onChange,
  options,
  disabled = false,
  required = false,
  className = "",
  direction = "horizontal",
  style = "default",
}: RadioGroupProps) {
  const hasLabel = label !== undefined;

  return (
    <div className={`relative ${className}`}>
      {/* 그룹 라벨 */}
      {hasLabel && (
        <div className="h-6 mb-1">
          <label className="block text-base font-normal text-[#414651]">
            {required && <span className="text-red-500 mr-1">*</span>}
            {label}
          </label>
        </div>
      )}

      {/* 라디오 버튼들 */}
      <div
        className={`
          flex gap-4
          ${direction === "vertical" ? "flex-col" : "flex-row"}
        `}
      >
        {options.map(option => (
          <RadioBtn
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={value === option.value}
            onChange={onChange}
            disabled={disabled}
            required={false} // 그룹 레벨에서만 required 처리
            style={style}
          />
        ))}
      </div>
    </div>
  );
}

// 기본 export는 RadioGroup으로
export default RadioGroup;
